import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import { Delete, Warning } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const recordApi = (record) => (
  String(record?.inventoryDomain || '').toUpperCase() === 'LOAN'
    ? (path) => `/api/store/loan${path}`
    : (path) => `/api/store${path}`
);

const receivingApi = (dep) => (
  String(dep?.inventoryDomain || '').toUpperCase() === 'LOAN'
    ? (path) => `/api/store/loan${path}`
    : (path) => `/api/store${path}`
);

const recipientLabel = (record) => {
  if (record?.loanDirection === 'LOAN_OUT') {
    const company = String(record?.counterpartyCompany || '').trim();
    const contact = String(record?.counterpartyContactName || '').trim();
    if (contact && company) return `${contact} (${company})`;
    return contact || company || 'N/A';
  }
  const first = record?.issuedTo?.firstName;
  const last = record?.issuedTo?.lastName;
  const name = `${first || ''} ${last || ''}`.trim();
  if (name) return name;
  const ext = String(record?.issuedToExternalName || '').trim();
  if (ext) {
    const org = String(record?.issuedToExternalCompany || '').trim();
    return org ? `${ext} (${org})` : ext;
  }
  return 'N/A';
};

/**
 * Cancel issuance & restore stock dialog (Store Manager / AM / Super Admin).
 * Confirms, reverses inventory effects, then permanently deletes the record.
 * If linked records block the delete, they are listed with their own delete action.
 */
const ForceDeleteIssuanceDialog = ({ open, issuance, onClose, onDeleted }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [dependencies, setDependencies] = useState(null);
  const [deletingDepId, setDeletingDepId] = useState('');

  useEffect(() => {
    if (open) {
      setDependencies(null);
      setDeletingDepId('');
      setLoading(false);
    }
  }, [open, issuance?._id]);

  const handleForceDelete = useCallback(async () => {
    if (!issuance?._id) return;
    setLoading(true);
    try {
      const api = recordApi(issuance);
      const res = await axios.delete(api(`/issuances/${issuance._id}/force`));
      showToast(res.data?.message || 'Issuance cancelled and stock restored', 'success');
      setDependencies(null);
      if (onDeleted) onDeleted();
      if (onClose) onClose();
    } catch (error) {
      const deps = error.response?.data?.dependencies;
      if (error.response?.status === 400 && deps?.hasDependencies) {
        setDependencies(deps);
        showToast(
          error.response?.data?.message || 'Linked records must be deleted first',
          'warning'
        );
      } else {
        showToast(error.response?.data?.message || 'Failed to cancel issuance / restore stock', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [issuance, onClose, onDeleted, showToast]);

  const handleDeleteDependency = useCallback(async (dep) => {
    if (!dep?.id) return;
    setDeletingDepId(String(dep.id));
    try {
      const api = receivingApi(dep);
      await axios.delete(api(`/receiving/${dep.id}`));
      showToast('Linked record deleted', 'success');
      setDependencies((prev) => {
        if (!prev) return prev;
        const receivingRecords = (prev.receivingRecords || []).filter(
          (r) => String(r.id) !== String(dep.id)
        );
        const hasDependencies = receivingRecords.length > 0;
        return { ...prev, receivingRecords, hasDependencies };
      });
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete linked record', 'error');
    } finally {
      setDeletingDepId('');
    }
  }, [showToast]);

  const linkedRecords = dependencies?.receivingRecords || [];
  const stillBlocked = dependencies?.hasDependencies && linkedRecords.length > 0;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <Warning color="error" /> Cancel Issuance & Restore Stock
      </DialogTitle>
      <DialogContent dividers>
        {issuance && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Issued To</Typography>
            <Typography variant="body1" gutterBottom>{recipientLabel(issuance)}</Typography>
            <Typography variant="body2" color="text.secondary">Details</Typography>
            <Typography variant="body1">
              {issuance.items?.length || 0} item(s)
              {issuance.status ? ` · ${issuance.status}` : ''}
              {issuance.issuedDate ? ` · issued ${new Date(issuance.issuedDate).toLocaleDateString()}` : ''}
            </Typography>
          </Box>
        )}

        {!stillBlocked && (
          <Alert severity="error" icon={<Warning />}>
            This cancels the issuance and restores stock to inventory
            (quantity returned, status set back to IN_INVENTORY; any returns are undone).
            Use this when the wrong aircraft stock was issued by mistake. This action cannot be undone.
          </Alert>
        )}

        {stillBlocked && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              The following linked record(s) reference this issuance and must be deleted first.
            </Alert>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Linked Receiving</TableCell>
                    <TableCell>Purpose</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkedRecords.map((dep) => (
                    <TableRow key={String(dep.id)}>
                      <TableCell>
                        {dep.receivingDate ? new Date(dep.receivingDate).toLocaleDateString() : '—'}
                        {` · ${dep.itemCount || 0} item(s)`}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={dep.purpose || 'RECEIVING'} />
                      </TableCell>
                      <TableCell>{dep.status || '—'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<Delete fontSize="small" />}
                          disabled={deletingDepId === String(dep.id)}
                          onClick={() => handleDeleteDependency(dep)}
                        >
                          {deletingDepId === String(dep.id) ? 'Deleting...' : 'Delete'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleForceDelete}
          disabled={loading || deletingDepId !== ''}
        >
          {loading ? 'Restoring stock...' : (stillBlocked ? 'Retry cancel' : 'Cancel & restore stock')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForceDeleteIssuanceDialog;
